import { useState, useEffect, Suspense } from "react";

import PropTypes from "prop-types";
import AiCallSummary from "@shared/AiAssistant/AiCallSummary";
import { lazyAiComponent } from "@shared/AiAssistant/lazyAiComponent";
import TextToggle from "@shared/v2/TextToggle";
import Avatar from "@shared/v2/Avatar";
import Button from "@shared/v2/Button";
import Pagination from "@shared/v2/Pagination";
import ErrorBoundary from "@shared/v2/ErrorBoundary";
import TextButton from "@shared/v2/TextButton";
import Typography from "@shared/v2/Typography";
import {
  ChevronDown,
  ChevronRight,
  PlaySolidV6,
  PhoneMissedSolidV6,
  PhoneArrowDownLeftSolidV6,
  PlusSolidV6,
  SpinnerSolidV6,
  DesktopSolidV6,
  BookSolidV6,
} from "@shared/v2/Icomoon";
import TableHeader from "./TableHeader";
import TableCell from "./TableCell";
import Arrows from "./Arrows";
import { durationDisplay } from "./helpers";
import { Section } from "../../Reports/components/BusinessDashboard";
import ReportsFilters from "./ReportsFilters";
import TrainingResource from "./TrainingResource";
import TrainingResourceVideoModal from "./TrainingResourceVideoModal";
import { useFeatureFlags } from "../../reducers/layoutReducer/selectors";

const LOGS_OPTIONS = [
  { label: "Calls", value: "calls" },
  { label: "Texts", value: "texts" },
];

const TrackingNumbersLogs = ({
  addTrackingNumber,
  agents,
  isAdmin,
  sort,
  setSort,
  offset,
  textoffset,
  setOffset,
  setTextOffset,
  logsCount,
  textlogsCount,
  textLogs,
  callLogs,
  selectedTrackingNumbers,
  trackingNumbers,
  handleTrackingNumberFilterOnChange,
  minDate,
  maxDate,
  setMinDate,
  setMaxDate,
  chosenView,
  logFilters,
  setLogFilters,
  loadingCalls,
  loadingTexts,
  loadingCounts,
}) => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState("calls");
  const handleSort = (field) => {
    let sortDirection = sort[1];

    if (field === sort[0]) {
      sortDirection = sortDirection === "asc" ? "desc" : "asc";
    }

    setSort([field, sortDirection]);
  };

  const changeSelectedLog = (val) => {
    setSelectedLog(val);
  };

  const renderEmptyDatefulState = (type) => (
    <div className="tw-my-[32px] tw-text-18px tw-text-gray-50 tw-text-center">
      <div className="tw-font-bold tw-pb-[8px]">There are no {type} logs for these dates.</div>
      Select a different timeframe to see your {type} logs.
    </div>
  );

  const renderEmptyState = (type) => (
    <div className="tw-my-[32px] tw-text-18px tw-text-gray-50 tw-text-center">
      <div className="tw-font-bold tw-pb-[8px]">You have not received any {type}s yet. </div>
      Inbound text logs will appear here once someone has {type}ed your tracking number.
    </div>
  );

  const renderCallLogs = () => {
    if (trackingNumbers.length === 0)
      return <LogEmptyState addTrackingNumber={addTrackingNumber} isAdmin={isAdmin} />;

    if (callLogs.length === 0) {
      return minDate || maxDate ? renderEmptyDatefulState("call") : renderEmptyState("call");
    }

    return (
      <TrackingNumbersCallLogTable
        logs={callLogs}
        handleSort={handleSort}
        sortField={sort[0]}
        sortDirection={sort[1]}
        totalCount={logsCount}
        currentOffset={offset}
        pageSize={25}
        onChangePage={setOffset}
        isAdmin={isAdmin}
        trackingNumbers={trackingNumbers}
        loadingCounts={loadingCounts}
      />
    );
  };

  const renderTextLogs = () => {
    if (trackingNumbers.length === 0) return <LogEmptyState addTrackingNumber={addTrackingNumber} />;

    if (textLogs.length === 0) {
      return minDate || maxDate ? renderEmptyDatefulState("text") : renderEmptyState("text");
    }

    return (
      <TrackingNumbersTextLogTable
        logs={textLogs}
        totalCount={textlogsCount}
        currentOffset={textoffset}
        pageSize={25}
        onChangePage={setTextOffset}
        isAdmin={isAdmin}
        loadingCounts={loadingCounts}
      />
    );
  };

  return (
    <div className="tw-p-[16px] tw-flex tw-flex-col tw-items-stretch tw-flex-wrap tw-gap-8px tw-bg-tinted-gray-50">
      <ReportsFilters
        selectedTrackingNumbers={selectedTrackingNumbers}
        trackingNumbers={trackingNumbers}
        handleTrackingNumberFilterOnChange={handleTrackingNumberFilterOnChange}
        minDate={minDate}
        maxDate={maxDate}
        setMinDate={setMinDate}
        setMaxDate={setMaxDate}
        chosenView={chosenView}
        logFilters={logFilters}
        setLogFilters={setLogFilters}
        agents={agents}
        selectedLog={selectedLog}
        isDisabled={callLogs.length === 0 && textLogs.length === 0}
      />

      <div className="tw-relative tw-bg-white tw-rounded-[8px] tw-border-solid tw-border-[1px] tw-border-neutral-gray-10 bs-xs:tw-p-[15px] tw-flex tw-flex-col tw-p-[32px]">
        <div className="tw-flex tw-flex-col tw-gap-[16px] tw-pb-[32px]">
          <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-[16px]">
            <div className="tw-flex tw-items-center tw-gap-[16px]">
              <TextToggle
                options={LOGS_OPTIONS}
                value={selectedLog}
                onChange={(e) => changeSelectedLog(e.target.value)}
              />
            </div>
          </div>
        </div>
        {/* eslint-disable-next-line no-nested-ternary */}
        {loadingCalls || loadingTexts ? (
          <div className="tw-my-[88px] tw-flex tw-items-center tw-justify-center tw-h-full">
            <SpinnerSolidV6 className="tw-h-[32px] tw-w-[32px] tw-animate-spin" />
          </div>
        ) : selectedLog === "calls" ? (
          renderCallLogs()
        ) : (
          renderTextLogs()
        )}
      </div>
      <div className="tw-bg-white tw-rounded-[8px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-p-[32px] tw-flex tw-flex-col tw-gap-[32px]">
        {/* @ts-expect-error props */}
        <Typography variant="headline2">Training & Resources</Typography>
        <div className="tw-flex tw-gap-[24px] tw-flex-wrap">
          <TrainingResource
            action={
              <Button
                className="tw-whitespace-nowrap"
                schema="secondary"
                onClick={() => setShowVideoModal(true)}
              >
                Watch Video
              </Button>
            }
            description="Watch this video to get a high-level look at how Phone System can help you close more deals."
            icon={<PlaySolidV6 size="l" />}
            title="Video Overview"
          />
          <TrainingResource
            action={
              <Button
                className="tw-whitespace-nowrap"
                schema="secondary"
                onClick={() =>
                  window.open(
                    "https://www.brivityknowledge.com/docs/what-is-the-brivity-phone-system",
                    "_blank",
                  )
                }
              >
                Learn More
              </Button>
            }
            description="Learn phone system best practices to make the most of your inbound leads."
            icon={<DesktopSolidV6 size="l" />}
            title="Phone System Practices"
          />
          <TrainingResource
            action={
              <Button
                className="tw-whitespace-nowrap"
                schema="secondary"
                onClick={() =>
                  window.open("https://www.brivityknowledge.com/docs/phone-system-faq", "_blank")
                }
              >
                Learn More
              </Button>
            }
            description="Visit the Brivity Knowledge Base to gain access to helpful videos and articles on Phone System and more."
            icon={<BookSolidV6 size="l" />}
            title="Phone System FAQ"
          />
        </div>
        <TrainingResourceVideoModal open={showVideoModal} onClose={() => setShowVideoModal(false)} />
      </div>
    </div>
  );
};

TrackingNumbersLogs.propTypes = {
  agents: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  sort: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSort: PropTypes.func.isRequired,
  offset: PropTypes.number.isRequired,
  textoffset: PropTypes.number.isRequired,
  setOffset: PropTypes.func.isRequired,
  setTextOffset: PropTypes.func.isRequired,
  logsCount: PropTypes.number.isRequired,
  loadingCalls: PropTypes.bool.isRequired,
  loadingTexts: PropTypes.bool.isRequired,
  loadingCounts: PropTypes.bool.isRequired,
  textlogsCount: PropTypes.number.isRequired,
  callLogs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  textLogs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  selectedTrackingNumbers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  trackingNumbers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handleTrackingNumberFilterOnChange: PropTypes.func.isRequired,
  minDate: PropTypes.string.isRequired,
  maxDate: PropTypes.string.isRequired,
  setMinDate: PropTypes.func.isRequired,
  addTrackingNumber: PropTypes.func.isRequired,
  setMaxDate: PropTypes.func.isRequired,
  chosenView: PropTypes.string.isRequired,
  logFilters: PropTypes.shape().isRequired,
  setLogFilters: PropTypes.func.isRequired,
};

const LogRow = ({ log, isCallRecordingEnabled, initOpen, elementId }) => {
  const featureFlags = useFeatureFlags();
  const [isVisible, setIsVisible] = useState(initOpen);

  const notMissed = log.outcome !== "Missed" && (log.voicemailUrl || log.callSid);
  return [
    <tr
      className={`tw-h-36px tw-leading-[24px] tw-border-1px tw-border-solid tw-border-x-0 tw-border-gray-10 ${isVisible ? "tw-bg-[#FAFAFA]" : ""} ${notMissed ? "tw-cursor-pointer hover:tw-bg-[#f5f7f7]" : ""}`}
      onClick={() => notMissed && setIsVisible(!isVisible)}
      id={elementId}
    >
      <TableCell>
        {notMissed ? (
          <span className="tw-text-gray-50">{isVisible ? <ChevronDown /> : <ChevronRight />}</span>
        ) : (
          ""
        )}
      </TableCell>
      <TableCell>
        {log.callerUrl ? (
          <TextButton
            className="tw-p-0 tw-capitalize"
            size="medium"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`${log.callerUrl}?tab=2`, "_blank");
            }}
          >
            {log.callerFullName ? log.callerFullName : log.callerPhone}
          </TextButton>
        ) : (
          <span>{log.callerPhone}</span>
        )}
      </TableCell>
      <TableCell>
        <span className={log.isNumberDeleted ? "tw-text-gray-30" : ""}>
          {log.trackingNumberName}
          {log.isNumberDeleted ? " (Deleted)" : ""}
        </span>
      </TableCell>
      <TableCell>
        {log.answererFullName ? (
          <div className="tw-flex tw-items-center">
            <Avatar
              className="tw-mr-12px"
              size="small"
              src={log.answererAvatarUrl}
              alt={log.answererFullName}
            />
            <span>{log.answererFullName}</span>
          </div>
        ) : (
          "--"
        )}
      </TableCell>
      <TableCell>
        {log.outcome === "Talked" && <TalkedOutcome outcome={log.outcome} />}
        {log.outcome === "Missed" && <MissedOutcome outcome={log.outcome} />}
        {log.outcome === "Voicemail" && <VoicemailOutcome outcome={log.outcome} />}
      </TableCell>
      <TableCell>{log.duration === 0 ? "--" : durationDisplay(log.duration)}</TableCell>
      <TableCell>{log.createdAt}</TableCell>
    </tr>,
    isVisible ? (
      <tr className={isVisible ? "" : "tw-hidden"}>
        <td colSpan={7}>
          <div className="tw-bg-gray-5 tw-p-[24px]">
            <Section className="tw-bg-white tw-p-24px">
              <AiCallSummary
                callSid={log.callSid}
                isCallRecordingEnabled={isCallRecordingEnabled}
                aiSummaryDisabled={
                  featureFlags.callRecordingAdjustments && log.answererAiCallSummary === false
                }
                showCallCoaching={featureFlags.aiCallAnalysis}
              />
            </Section>
          </div>
        </td>
      </tr>
    ) : null,
  ];
};

const TextLogRow = ({ log }) => {
  const [isVisible, setIsVisible] = useState(false);
  const isExpandable = log?.message && log?.message?.length > 70;

  const expandedClassName = isVisible ? "!tw-align-middle !tw-h-[38px]" : "";
  const expandedTdClassName = isVisible ? "!tw-align-top" : "";

  return [
    <tr
      className={`tw-align-middle tw-text-[14px] tw-border-1px tw-border-solid tw-border-x-0 tw-border-gray-10  ${isExpandable ? "tw-cursor-pointer hover:tw-bg-[#f5f7f7]" : ""}`}
      onClick={() => isExpandable && setIsVisible(!isVisible)}
    >
      <TableCell className={`${expandedTdClassName}`}>
        <div className={`${expandedClassName} ${isVisible ? "!tw-pt-[8.5px]" : ""}`}>
          {isExpandable ? (
            <span className="tw-text-gray-50">{isVisible ? <ChevronDown /> : <ChevronRight />}</span>
          ) : (
            ""
          )}
        </div>
      </TableCell>
      <TableCell className={`${expandedTdClassName}`}>
        <div className={`${expandedClassName} ${isVisible ? "!tw-pt-[8.5px]" : ""}`}>
          <TextButton
            className="tw-p-0 tw-capitalize"
            size="medium"
            onClick={(e) => {
              e.stopPropagation();
              if (log.leadUrl) {
                window.open(`${log.leadUrl}?tab=3&messageUUID=${log.messageUUID}`, "_blank");
              }
            }}
          >
            {log.leadFullName || log.phoneNumber || ""}
          </TextButton>
        </div>
      </TableCell>
      <TableCell className={`${expandedTdClassName}`}>
        <div
          className={`${expandedClassName} ${isVisible && !log.trackingPhoneNumber ? "!tw-pt-[8.5px]" : ""}`}
        >
          <span className={`tw-text-[12px] ${log.isNumberDeleted ? "tw-text-gray-30" : ""}`}>
            {log.trackingNumberName}
            {log.isNumberDeleted ? " (Deleted)" : ""}
            <div>{log.trackingPhoneNumber || ""}</div>
          </span>
        </div>
      </TableCell>
      <TableCell className={`${expandedTdClassName}`}>
        <div className={`${expandedClassName} ${isVisible ? "!tw-pt-[6.5px]" : ""}`}>
          {log.recipientFullName ? (
            <div className="tw-flex tw-items-center">
              <Avatar
                className="tw-mr-12px"
                size="small"
                src={log.recipientAvatarUrl}
                alt={log.recipientFullName}
              />
              <span>{log.recipientFullName}</span>
            </div>
          ) : (
            "--"
          )}
        </div>
      </TableCell>
      <TableCell className="tw-max-w-[500px]">
        <div
          className={` ${isVisible ? "!tw-pt-[8.5px] !tw-whitespace-normal" : "tw-overflow-ellipsis tw-whitespace-nowrap tw-overflow-hidden"}`}
        >
          {log.message || ""}
        </div>
      </TableCell>
      <TableCell className={`${expandedTdClassName}`}>
        <div className={`${expandedClassName}`}>
          <span className="tw-text-[12px]">
            {log?.createdAtDate}
            <div>{log?.createdAtTime}</div>
          </span>
        </div>
      </TableCell>
    </tr>,
  ];
};

const LogEmptyState = ({ addTrackingNumber, isAdmin }) => (
  <Section className="tw-p-[32px]">
    <div className="tw-flex tw-flex-col tw-mt-[66px] tw-mb-[50px] tw-items-center tw-justify-center tw-gap-[8px]">
      <span className="tw-text-[18px] tw-leading-[24px] tw-font-bold tw-text-gray-50 tw-whitespace-nowrap">
        Looks like you have no active Tracking Numbers.
      </span>
      <span className="tw-text-[18px] tw-leading-[24px] tw-font-normal tw-text-gray-50 tw-max-w-[750px] tw-text-center">
        Make it easier to connect with your leads, gain insight into your marketing campaigns, and understand
        your ROI like never before with Tracking Numbers.
      </span>
      <div className="tw-pt-[12px] tw-pb-[16px]">
        <img
          className="tw-block tw-w-full tw-h-full tw-object-cover"
          src="/assets/phone-sysytem-empty-log.svg"
          alt="Phone system"
        />
      </div>
      {isAdmin && (
        <Button size="medium" onClick={addTrackingNumber}>
          <div className="tw-flex tw-justify-center tw-items-center tw-gap-6px">
            <PlusSolidV6 />
            Add Tracking Number
          </div>
        </Button>
      )}
    </div>
  </Section>
);

LogEmptyState.propTypes = {
  addTrackingNumber: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

const TrackingNumbersCallLogTable = ({
  logs,
  handleSort,
  sortField,
  sortDirection,
  totalCount,
  currentOffset,
  pageSize,
  onChangePage,
  trackingNumbers,
  loadingCounts,
}) => {
  const searchParams = new URLSearchParams(window.location.search);
  const callLogId = searchParams.get("call_log_id");
  const totalPages = Math.ceil(totalCount / pageSize);
  const currentPage = currentOffset ? currentOffset / pageSize + 1 : 1;
  const nextPage = currentPage === totalPages ? currentPage : currentPage + 1;
  const previousPage = currentPage === 1 ? currentPage : currentPage - 1;

  const onGoToPage = (pageNumber) => {
    const newOffset = (pageNumber - 1) * pageSize;
    onChangePage(newOffset);
  };

  useEffect(() => {
    if (callLogId) {
      const element = document.getElementById(`log-${callLogId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [callLogId, logs]);

  return (
    <div>
      <div className="tw-width-auto tw-relative tw-flex tw-flex-col tw-flex-nowrap tw-overflow-auto tw-grow tw-shrink-0">
        <table className="tw-border-collapse tw-overflow-scroll tw-table-auto tw-flex-nowrap">
          <thead>
            <tr className="tw-text-12px tw-text-gray-75 tw-border-[1px] tw-border-solid tw-border-b-[1px] tw-border-x-0 tw-border-gray-10">
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <th />
              <TableHeader>Caller</TableHeader>
              <TableHeader>Tracking Number</TableHeader>
              <TableHeader sortable onClick={() => handleSort("user_id")}>
                Answered by <Arrows sortBy={sortField === "user_id" ? sortDirection : null} />
              </TableHeader>
              <TableHeader>Outcome</TableHeader>
              <TableHeader sortable onClick={() => handleSort("duration")}>
                Duration <Arrows sortBy={sortField === "duration" ? sortDirection : null} />
              </TableHeader>
              <TableHeader sortable onClick={() => handleSort("id")}>
                Date <Arrows sortBy={sortField === "id" ? sortDirection : null} />
              </TableHeader>
            </tr>
          </thead>
          <tbody>
            {logs?.map((log) => (
              <LogRow
                key={`log-${log.callLogId}`}
                elementId={`log-${log.callLogId}`}
                log={log}
                initOpen={callLogId && Number(callLogId) === Number(log.callLogId)}
                isCallRecordingEnabled={
                  trackingNumbers?.find((tn) => tn.meta.name === log.trackingNumberName)?.meta
                    .inbound_recording !== "disabled" || false
                }
              />
            ))}
          </tbody>
        </table>
      </div>
      {logs?.length > 0 && !loadingCounts ? (
        <div className="tw-flex tw-justify-end tw-mt-32px">
          <Pagination
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={previousPage}
            totalPages={totalPages}
            onGoToPage={onGoToPage}
          />
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

TrackingNumbersCallLogTable.propTypes = {
  logs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handleSort: PropTypes.func.isRequired,
  sortField: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  totalCount: PropTypes.number.isRequired,
  currentOffset: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  trackingNumbers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  loadingCounts: PropTypes.bool.isRequired,
};

const TrackingNumbersTextLogTable = ({
  logs,
  totalCount,
  currentOffset,
  pageSize,
  onChangePage,
  loadingCounts,
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const currentPage = currentOffset ? currentOffset / pageSize + 1 : 1;
  const nextPage = currentPage === totalPages ? currentPage : currentPage + 1;
  const previousPage = currentPage === 1 ? currentPage : currentPage - 1;

  const onGoToPage = (pageNumber) => {
    const newOffset = (pageNumber - 1) * pageSize;
    onChangePage(newOffset);
  };

  return (
    <div>
      <div className="tw-width-auto tw-relative tw-flex tw-flex-col tw-flex-nowrap tw-overflow-auto tw-grow tw-shrink-0">
        <table className="tw-border-collapse tw-overflow-scroll tw-table-auto tw-flex-nowrap">
          <thead>
            <tr className="tw-text-12px tw-text-gray-75 tw-border-[1px] tw-border-solid tw-border-b-[1px] tw-border-x-0 tw-border-gray-10">
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <th />
              <TableHeader>Sender</TableHeader>
              <TableHeader>Tracking Number</TableHeader>
              <TableHeader>Recipient</TableHeader>
              <TableHeader>Text</TableHeader>
              <TableHeader>Date</TableHeader>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <TextLogRow key={`log-${log.callLogId}`} log={log} />
            ))}
          </tbody>
        </table>
      </div>
      {logs?.length > 0 && !loadingCounts ? (
        <div className="tw-flex tw-justify-end tw-mt-32px">
          <Pagination
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={previousPage}
            totalPages={totalPages}
            onGoToPage={onGoToPage}
          />
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

TrackingNumbersTextLogTable.propTypes = {
  logs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  totalCount: PropTypes.number.isRequired,
  currentOffset: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  loadingCounts: PropTypes.bool.isRequired,
};

const AudioPlayer = lazyAiComponent("AudioPlayer");

const ExpandedInfo = ({ recordingLink }) => (
  <div className="tw-bg-gray-10 tw-p-[24px]">
    <Section className="tw-bg-white">
      <ErrorBoundary>
        <Suspense fallback={null}>
          <AudioPlayer src={recordingLink} />
        </Suspense>
      </ErrorBoundary>
    </Section>
  </div>
);

ExpandedInfo.propTypes = {
  recordingLink: PropTypes.string.isRequired,
};

const TalkedOutcome = ({ outcome }) => (
  <div className="tw-flex tw-items-center tw-text-[#36b25f]">
    <PhoneArrowDownLeftSolidV6 className="tw-mr-8px tw-w-16px tw-h-16px" />
    {outcome}
  </div>
);

TalkedOutcome.propTypes = {
  outcome: PropTypes.string.isRequired,
};

const MissedOutcome = ({ outcome }) => (
  <div className="tw-flex tw-items-center tw-text-[#e5342e]">
    <PhoneMissedSolidV6 className="tw-mr-8px tw-w-16px tw-h-16px" />
    {outcome}
  </div>
);

MissedOutcome.propTypes = {
  outcome: PropTypes.string.isRequired,
};

const VoicemailOutcome = ({ outcome }) => (
  <div className="tw-flex tw-items-center tw-text-gray-75">
    <PlaySolidV6 className="tw-mr-8px tw-w-16px tw-h-16px" />
    {outcome}
  </div>
);

VoicemailOutcome.propTypes = {
  outcome: PropTypes.string.isRequired,
};

export default TrackingNumbersLogs;
